import React, { useState } from "react";
import {
  Users,
  Globe,
  Target,
  Calendar,
  PieChart,
  Activity,
  Shield,
  Zap,
  BookOpen,
  Server,
  Layers,
  CheckCircle,
  X,
  ArrowRight,
} from "lucide-react";
import Modal from "../ui/Modal";
import campaignData from "../../data/campaignData.json";

// --- SUB-COMPONENTS (From your original code) ---

// A clickable wrapper for each card
const ClickableCard = ({ children, onClick, className = "" }) => (
  <div
    onClick={onClick}
    className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.01] hover:bg-slate-50/50 rounded-xl ${className}`}
  >
    {children}
  </div>
);

const StatBlock = ({ label, value, subtext, color = "blue" }) => (
  <div
    className={`bg-${color}-50 p-4 rounded-xl border border-${color}-100 flex flex-col justify-between h-full pointer-events-none`}
  >
    <div className="flex items-start justify-between">
      <span
        className={`text-${color}-600 font-bold text-xs uppercase tracking-wider`}
      >
        {label}
      </span>
      {color === "emerald" && (
        <Activity size={16} className="text-emerald-500" />
      )}
    </div>
    <div className="mt-2">
      <span className={`text-2xl font-bold text-${color}-900`}>{value}</span>
      {subtext && (
        <p className={`text-xs text-${color}-700 mt-1 font-medium`}>
          {subtext}
        </p>
      )}
    </div>
  </div>
);

// --- MAIN COMPONENT ---
const CampaignOverviewCard = () => {
  const [selectedCard, setSelectedCard] = useState(null);

  // Destructure new data sections (with fallbacks)
  const execData = campaignData?.executive_overview || {};
  const governanceData = campaignData?.system_governance || {
    definition: { is: [], is_not: [] },
  };

  const handleCloseModal = () => {
    setSelectedCard(null);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* SECTION 1: EXECUTIVE HERO (New Strategic Frame) */}
      <div className="bg-slate-900 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>

        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-emerald-500/20 rounded-lg backdrop-blur-sm border border-emerald-500/30">
                  <Shield className="w-6 h-6 text-emerald-400" />
                </div>
                <span className="text-emerald-400 font-bold tracking-wide uppercase text-xs">
                  Governance & Execution System
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                Nurture Optimization Center
              </h1>
              <p className="text-slate-300 text-lg leading-relaxed max-w-3xl">
                {execData.mission ||
                  "A governed system that turns verified segments into executable nurture streams."}
              </p>
            </div>

            {/* High-Level Impact Stats */}
            <div className="flex gap-4">
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 backdrop-blur-sm text-center min-w-[100px]">
                <span className="block text-2xl font-bold text-white">440</span>
                <span className="text-xs text-slate-400 uppercase font-medium">
                  Total Goal
                </span>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 backdrop-blur-sm text-center min-w-[100px]">
                <span className="block text-2xl font-bold text-emerald-400">
                  100%
                </span>
                <span className="text-xs text-slate-400 uppercase font-medium">
                  LEAP Valid
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: OPERATIONAL DASHBOARD (Your Original High-Value Layout) */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        {/* HEADER */}
        <div className="flex justify-between items-start mb-6 border-b border-slate-100 pb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-slate-900 text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest">
                FY25 Q4-Q1
              </span>
              <span className="text-slate-400 text-sm font-medium">/</span>
              <span className="text-emerald-600 text-sm font-bold uppercase tracking-wide">
                Active Campaign
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900">
              APAC Global Skills & Productivity Initiative
            </h2>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">Live Volume Goal</p>
            <p className="text-3xl font-black text-slate-800">
              440{" "}
              <span className="text-lg text-slate-400 font-normal">Leads</span>
            </p>
          </div>
        </div>

        {/* ROW 1: THE STATS (Clickable) */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <ClickableCard onClick={() => setSelectedCard("territory_split")}>
            <StatBlock
              label="Territory Split"
              value="50/50"
              subtext="220 SEA / 220 India"
              color="blue"
            />
          </ClickableCard>
          <ClickableCard onClick={() => setSelectedCard("phase_1")}>
            <StatBlock
              label="Phase 1 (6 Weeks)"
              value="150 Leads"
              subtext="Starts Dec 08, 2025"
              color="indigo"
            />
          </ClickableCard>
          <ClickableCard onClick={() => setSelectedCard("phase_2")}>
            <StatBlock
              label="Phase 2 (6 Weeks)"
              value="290 Leads"
              subtext="Starts Jan 19, 2026"
              color="purple"
            />
          </ClickableCard>
          <ClickableCard onClick={() => setSelectedCard("asset_focus")}>
            <StatBlock
              label="Asset Focus"
              value="2 Core Assets"
              subtext="Skills Report / Productivity"
              color="emerald"
            />
          </ClickableCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* COL 1: GEOGRAPHIC "LEAGUE" BREAKDOWN (Clickable) */}
          <ClickableCard
            onClick={() => setSelectedCard("region_allocation")}
            className="h-full"
          >
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 h-full pointer-events-none">
              <div className="flex items-center gap-2 mb-4">
                <Globe size={18} className="text-slate-500" />
                <h3 className="font-bold text-slate-800 text-sm uppercase">
                  Region Allocation
                </h3>
              </div>

              <div className="space-y-4">
                {/* SEA Breakdown */}
                <div>
                  <div className="flex justify-between text-xs font-bold text-slate-700 mb-2">
                    <span>SEA (Southeast Asia)</span>
                    <span>220 Total</span>
                  </div>
                  <div className="bg-white rounded border border-slate-200 p-2 space-y-2">
                    {[
                      "Indonesia",
                      "Thailand",
                      "Philippines",
                      "Singapore",
                      "Malaysia",
                    ].map((country) => (
                      <div
                        key={country}
                        className="flex justify-between text-xs"
                      >
                        <span className="text-slate-500">{country}</span>
                        <span className="font-mono font-medium text-slate-700">
                          44
                        </span>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-slate-100 mt-2">
                      <p className="text-[10px] text-slate-400 italic">
                        Note: 80% multi-market coverage
                      </p>
                    </div>
                  </div>
                </div>

                {/* India Breakdown */}
                <div>
                  <div className="flex justify-between text-xs font-bold text-slate-700 mb-2">
                    <span>India (Concentrated)</span>
                    <span>220 Total</span>
                  </div>
                  <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                    <div className="bg-blue-600 h-full w-full"></div>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 italic">
                    Enterprise volume focus
                  </p>
                </div>
              </div>
            </div>
          </ClickableCard>

          {/* COL 2: THE "ROSTER" (Targeting) (Clickable) */}
          <ClickableCard
            onClick={() => setSelectedCard("segment_targeting")}
            className="lg:col-span-2 h-full"
          >
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 h-full pointer-events-none">
              <div className="flex items-center gap-2 mb-4">
                <Target size={18} className="text-slate-500" />
                <h3 className="font-bold text-slate-800 text-sm uppercase">
                  Segment Targeting Strategy
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Financial Services Roster */}
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      Financial Services
                    </span>
                    <span className="text-[10px] text-red-500 font-medium">
                      Excludes Tech Titles
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "CLO",
                      "CHRO",
                      "SVP L&D",
                      "Chief People Officer",
                      "VP Leadership",
                      "Global Head L&D",
                    ].map((title) => (
                      <span
                        key={title}
                        className="text-[11px] bg-slate-50 border border-slate-100 px-2 py-1 rounded text-slate-600"
                      >
                        {title}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Tech Roster */}
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      Technology (India)
                    </span>
                    <span className="text-[10px] text-purple-600 font-medium">
                      35% L&D / 30% HR / 30% Tech
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "CTO",
                      "CIO",
                      "VP AI",
                      "VP Data",
                      "Head of Cloud",
                      "EVP Engineering",
                      "Head of Talent",
                    ].map((title) => (
                      <span
                        key={title}
                        className="text-[11px] bg-slate-50 border border-slate-100 px-2 py-1 rounded text-slate-600"
                      >
                        {title}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Phase 1 Stats Row */}
                <div className="col-span-1 md:col-span-2 bg-white p-3 rounded-lg border border-slate-200 flex justify-between items-center mt-2">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-bold text-slate-400 uppercase">
                      Phase 1 Mix:
                    </span>
                    <div className="flex gap-3">
                      <span className="text-xs font-medium text-slate-700">
                        L&D: <b className="text-slate-900">60</b>
                      </span>
                      <span className="text-xs font-medium text-slate-700">
                        HR: <b className="text-slate-900">60</b>
                      </span>
                      <span className="text-xs font-medium text-slate-700">
                        Tech: <b className="text-slate-900">20</b>
                      </span>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400">
                    Pacing: Dec 21 - Feb 2
                  </div>
                </div>
              </div>
            </div>
          </ClickableCard>
        </div>
      </div>

      {/* SECTION 3: SYSTEM METHODOLOGY (New Workflow & Governance) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Workflow Steps */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Layers size={18} className="text-emerald-500" /> System Workflow
          </h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-xs font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded">
                01
              </span>
              <div>
                <h4 className="text-sm font-bold text-slate-800">
                  Segment (Who)
                </h4>
                <p className="text-xs text-slate-500">
                  Define Region, Vertical, Persona, Stage.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xs font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded">
                02
              </span>
              <div>
                <h4 className="text-sm font-bold text-slate-800">
                  Blueprint (Why)
                </h4>
                <p className="text-xs text-slate-500">
                  Choose narrative & LEAP advisory standards.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xs font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded">
                03
              </span>
              <div>
                <h4 className="text-sm font-bold text-slate-800">
                  Stream (How)
                </h4>
                <p className="text-xs text-slate-500">
                  Auto-generate cadence, touches, and ops governance.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xs font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded">
                04
              </span>
              <div>
                <h4 className="text-sm font-bold text-slate-800">
                  Export (Build)
                </h4>
                <p className="text-xs text-slate-500">
                  Output Marketo-ready tokens, wait steps, and QA checklists.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Governance Rules */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Shield size={18} className="text-emerald-500" /> System Governance
          </h3>
          <div className="grid grid-cols-1 gap-6">
            <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100">
              <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2 flex items-center gap-1">
                <CheckCircle size={12} /> This System IS:
              </h4>
              <ul className="space-y-1">
                {governanceData.definition?.is?.map((item, i) => (
                  <li key={i} className="text-xs text-emerald-900 font-medium">
                    • {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-50/50 rounded-lg p-3 border border-slate-100">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <X size={12} /> This System IS NOT:
              </h4>
              <ul className="space-y-1">
                {governanceData.definition?.is_not?.map((item, i) => (
                  <li key={i} className="text-xs text-slate-500">
                    • {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL COMPONENT */}
      <Modal
        isOpen={selectedCard !== null}
        onClose={handleCloseModal}
        content={selectedCard ? campaignData[selectedCard] : null}
      />
    </div>
  );
};

export default CampaignOverviewCard;
